import { useCallback, useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import {
  Box,
  Button,
  Collapse,
} from "@mui/material";
import MicrosoftIcon from "@mui/icons-material/Microsoft";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import EntraLoginFlow from "./EntraLoginFlow";
import {
  isEntraEnabled,
  isMicrosoftLoginDisabled,
  loginRequest,
  msalConfig,
} from "../../auth/msalConfig";
import { postPlusInternalLogin } from "../../services/apiService";
import { setUserData } from "../../redux/slices/userDataSlice";
import FormTextField from "../../components/textField/formTextField";
import ToastNotification from "../../components/toastNotification";
import "../../components/textField/styles.css";
import "./styles.css";

const legacyLoginUrl = (process.env.REACT_APP_LOGIN_SERALOGIN_URL || "").trim();

const LegacyLoginRedirect = () => {
  useEffect(() => {
    if (legacyLoginUrl) {
      window.location.replace(legacyLoginUrl);
    }
  }, []);

  return (
    <main className="plus-login-page">
      <section className="plus-login-card">
        <div className="plus-login-brand">
          <img src="/images/logo.png" alt="Seralogin Plus" />
        </div>
        <div className="plus-login-info-alert">
          Redirigiendo a inicio de sesion...
        </div>
      </section>
    </main>
  );
};

const PlusLoginScreen = ({ msalInstance }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    severity: "info",
    summary: "",
    detail: "",
  });
  const [showToast, setShowToast] = useState(false);
  const [showInternalLogin, setShowInternalLogin] = useState(
    isMicrosoftLoginDisabled
  );
  const { control, handleSubmit } = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const showMessage = useCallback((severity, summary, detail) => {
    setToastConfig({ severity, summary, detail });
    setShowToast(true);
  }, []);

  const handleSessionResolved = useCallback(
    (sesion, azureUser = null, authData = {}) => {
      Cookies.set("sesion", JSON.stringify(sesion));
      setLoading(false);
      const roleList = authData?.roleList || [];
      const pageList = authData?.pageList || [];
      const authorityList = authData?.authorityList || [];
      const warning = authData?.warning || null;

      dispatch(
        setUserData({
          id: sesion?.id_usuario,
          authorityList,
          pageList,
          roleList,
          authSource: authData?.authSource || (azureUser ? "MICROSOFT" : "INTERNAL"),
        })
      );

      sessionStorage.setItem(
        "plusLoginSuccessMessage",
        "Bienvenido a Seralogin Plus."
      );
      if (warning) {
        sessionStorage.setItem("plusLoginWarningMessage", warning);
      } else {
        sessionStorage.removeItem("plusLoginWarningMessage");
      }
      if (azureUser) {
        sessionStorage.setItem("plusAzureUserInfo", JSON.stringify(azureUser));
      }
      navigate("/");
    },
    [dispatch, navigate, showMessage]
  );

  const handleError = useCallback(
    (message) => {
      setLoading(false);
      showMessage("error", "No fue posible ingresar", message);
    },
    [showMessage]
  );

  useEffect(() => {
    if (!msalInstance) {
      if (isMicrosoftLoginDisabled) {
        showMessage(
          "info",
          "Microsoft deshabilitado",
          "El acceso con Microsoft esta deshabilitado para este entorno."
        );
        return;
      }

      showMessage(
        "info",
        "Microsoft no configurado",
        "Microsoft Entra ID no esta configurado en este entorno."
      );
    }
  }, [msalInstance, showMessage]);

  const handleMicrosoftLogin = async () => {
    if (isMicrosoftLoginDisabled) {
      showMessage(
        "info",
        "Microsoft deshabilitado",
        "El acceso con Microsoft esta deshabilitado para este entorno."
      );
      return;
    }

    if (!msalInstance) {
      showMessage(
        "info",
        "Microsoft no configurado",
        "Microsoft Entra ID no esta configurado en este entorno."
      );
      return;
    }

    setLoading(true);
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (loginError) {
      setLoading(false);
      showMessage(
        "error",
        "Error con Microsoft",
        loginError?.message ||
          "No fue posible iniciar sesion con Microsoft."
      );
    }
  };

  const handleInternalLogin = async ({ email, password }) => {
    setLoading(true);

    try {
      const response = await postPlusInternalLogin(email, password);
      const sesion = response?.data?.sesion;

      if (!sesion) {
        throw new Error("La respuesta del servidor no incluyo la sesion.");
      }

      handleSessionResolved(sesion);
    } catch (loginError) {
      showMessage(
        "error",
        "Error de acceso",
        loginError?.response?.data?.error ||
          loginError?.message ||
          "No fue posible iniciar sesion."
      );
      setLoading(false);
    }
  };

  return (
    <main className="plus-login-page">
      <section className="plus-login-card">
        <ToastNotification
          severity={toastConfig.severity}
          summary={toastConfig.summary}
          detail={toastConfig.detail}
          showToast={showToast}
          setShowToast={setShowToast}
        />

        <div className="plus-login-brand">
          <img src="/images/logo.png" alt="Seralogin Plus" />
        </div>

        {!isMicrosoftLoginDisabled && msalInstance ? (
          <>
            <EntraLoginFlow
              onSessionResolved={handleSessionResolved}
              onError={handleError}
            />
            <br />
            <Button
              fullWidth
              variant="contained"
              size="large"
              startIcon={<MicrosoftIcon />}
              onClick={handleMicrosoftLogin}
              disabled={loading}
              className="plus-login-microsoft"
            >
              Acceso con Microsoft
            </Button>
            <br />
          </>
        ) : (
          <div className="plus-login-info-alert">
            {isMicrosoftLoginDisabled
              ? "El acceso con Microsoft esta deshabilitado para este entorno."
              : "Microsoft Entra ID no esta configurado en este entorno."}
          </div>
        )}

        <Button
          fullWidth
          type="button"
          variant="text"
          className="plus-login-internal-toggle"
          endIcon={
            <KeyboardArrowDownIcon
              className={showInternalLogin ? "toggle-icon-open" : ""}
            />
          }
          aria-expanded={showInternalLogin}
          onClick={() => setShowInternalLogin((current) => !current)}
        >
          Ingreso interno del equipo
        </Button>

        <Collapse in={showInternalLogin} timeout="auto" unmountOnExit>
          <Box
            component="form"
            onSubmit={handleSubmit(handleInternalLogin)}
            className="plus-login-internal-form"
          >
            <FormTextField
              name="email"
              control={control}
              label="Correo"
              type="email"
              autoComplete="email"
              margin="10px 0 0"
            />
            <br />
            <FormTextField
              name="password"
              control={control}
              label="Contrasena"
              type="password"
              autoComplete="current-password"
              margin="16px 0 0"
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              startIcon={<LockOutlinedIcon />}
              className="plus-login-submit"
            >
              Iniciar sesion
            </Button>
          </Box>
        </Collapse>

      </section>
    </main>
  );
};

const PlusLogin = () => {
  const msalInstance = useMemo(() => {
    if (isMicrosoftLoginDisabled || !isEntraEnabled) {
      return null;
    }
    return new PublicClientApplication(msalConfig);
  }, []);
  const [msalReady, setMsalReady] = useState(!isEntraEnabled);

  useEffect(() => {
    let mounted = true;

    if (!msalInstance) {
      return undefined;
    }

    msalInstance
      .initialize()
      .then(() => {
        if (mounted) {
          setMsalReady(true);
        }
      })
      .catch(() => {
        if (mounted) {
          setMsalReady(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [msalInstance]);

  if (isMicrosoftLoginDisabled) {
    return <LegacyLoginRedirect />;
  }

  if (!msalInstance || !msalReady) {
    return <PlusLoginScreen msalInstance={null} />;
  }

  return (
    <MsalProvider instance={msalInstance}>
      <PlusLoginScreen msalInstance={msalInstance} />
    </MsalProvider>
  );
};

export default PlusLogin;
